import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Expenses from "@/pages/Expenses";
import NewExpense from "@/pages/NewExpense";
import Approvals from "@/pages/Approvals";
import AdminUsers from "@/pages/AdminUsers";
import AdminCategories from "@/pages/AdminCategories";
import AdminHubs from "@/pages/AdminHubs";
import Account from "@/pages/Account";
import Payments from "@/pages/Payments";
import Broadband from "@/pages/Broadband";
import { Toaster } from "sonner";

const APPROVER_ROLES = ["manager", "finance", "admin"];
const ADMIN_ROLES = ["admin"];

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-zinc-50 text-zinc-500 font-mono text-sm">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  // Synchronous check during render: process Google OAuth return BEFORE any protected route runs.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/expenses" element={<Protected><Expenses /></Protected>} />
      <Route path="/expenses/new" element={<Protected><NewExpense /></Protected>} />
      <Route path="/payments" element={<Protected><Payments /></Protected>} />
      <Route path="/broadband" element={<Protected><Broadband /></Protected>} />
      <Route path="/account" element={<Protected><Account /></Protected>} />
      <Route
        path="/approvals"
        element={
          <Protected roles={APPROVER_ROLES}>
            <Approvals />
          </Protected>
        }
      />
      <Route
        path="/admin/users"
        element={
          <Protected roles={ADMIN_ROLES}>
            <AdminUsers />
          </Protected>
        }
      />
      <Route
        path="/admin/categories"
        element={
          <Protected roles={ADMIN_ROLES}>
            <AdminCategories />
          </Protected>
        }
      />
      <Route
        path="/admin/hubs"
        element={
          <Protected roles={ADMIN_ROLES}>
            <AdminHubs />
          </Protected>
        }
      />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}
